import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from './config';
import { Explain } from './Explain';

// Feed subscriptions — ONE component, two homes (the add-door fold,
// DECISIONS.md 2026-07-07): the lobby keeps it because refresh feeds the
// queue you're looking at, and the Add surface carries it because
// "subscribe" is a way of putting things in. Feeds are link objects +
// system subscribes_to edges; refresh imports through the exact same
// pipeline as a hand-pasted URL (dedupe makes it idempotent).
export default function FeedManager({ onRefreshed }) {
  const [feeds, setFeeds] = useState([]);
  const [feedUrl, setFeedUrl] = useState('');
  const [topic, setTopic] = useState('');
  const [note, setNote] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadFeeds = useCallback(async () => {
    try {
      const res = await apiFetch('/api/queue/feeds');
      if (res.ok) setFeeds((await res.json()).feeds || []);
    } catch {}
  }, []);

  useEffect(() => { loadFeeds(); }, [loadFeeds]);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!feedUrl.trim()) return;
    setNote(null);
    const res = await apiFetch('/api/queue/feeds', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // topic (optional): tags the FEED; every refresh flows the tag onto
      // newly imported items so viewsets can slice them
      body: JSON.stringify({ url: feedUrl.trim(), ...(topic.trim() ? { topic: topic.trim() } : {}) }),
    });
    if (res.ok) {
      setFeedUrl('');
      setTopic('');
      loadFeeds();
      setNote('Subscribed. Refresh to pull items.');
    } else {
      setNote((await res.json()).error || 'Could not subscribe');
    }
  };

  const unsubscribe = async (feedId) => {
    await apiFetch(`/api/queue/feeds/${feedId}`, { method: 'DELETE' });
    loadFeeds();
  };

  const refresh = async () => {
    setRefreshing(true);
    setNote(null);
    try {
      const res = await apiFetch('/api/queue/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (res.ok) {
        setNote(
          `Checked ${data.feeds} feed${data.feeds === 1 ? '' : 's'}: ${data.imported} new, ` +
          `${data.skipped_existing} already imported` +
          (data.failed?.length ? `, ${data.failed.length} failed` : '') + '.'
        );
        onRefreshed && onRefreshed();
      } else {
        setNote(data.error || 'Refresh failed');
      }
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="wv-panel">
      <div style={{ fontSize: 'var(--wv-fs-md)', fontWeight: 'bold', marginBottom: 'var(--wv-sp-3)' }}>
        <Explain concept="feeds">Feeds</Explain>
        <button
          onClick={refresh}
          disabled={refreshing || feeds.length === 0}
          style={{ marginLeft: 'var(--wv-sp-5)', fontSize: 'var(--wv-fs-sm)' }}
        >
          {refreshing ? 'Refreshing…' : 'Refresh now'}
        </button>
      </div>
      {feeds.map((f) => (
        <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--wv-sp-3)', fontSize: 'var(--wv-fs-md)', marginBottom: 'var(--wv-sp-1)' }}>
          <span style={{ fontFamily: 'monospace', color: 'var(--wv-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.url}</span>
          {f.topics?.map((t) => (
            <span key={t} className="wv-chip" style={{ fontSize: 'var(--wv-fs-xs)' }} title="Items from this feed arrive tagged with this topic">
              {t}
            </span>
          ))}
          <button onClick={() => unsubscribe(f.id)} title="Unsubscribe" style={{ border: 'none', background: 'none', color: 'var(--wv-muted)' }}>×</button>
        </div>
      ))}
      <form onSubmit={subscribe} style={{ display: 'flex', gap: 'var(--wv-sp-3)', marginTop: 'var(--wv-sp-3)', flexWrap: 'wrap' }}>
        <input
          value={feedUrl}
          onChange={(e) => setFeedUrl(e.target.value)}
          placeholder="RSS or Atom feed URL…"
          style={{ flex: 2, minWidth: '180px', fontSize: 'var(--wv-fs-md)', boxSizing: 'border-box' }}
        />
        <input
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="topic tag (optional)"
          title="Items imported from this feed arrive tagged with this topic, so viewsets can slice them"
          style={{ flex: 1, minWidth: '110px', fontSize: 'var(--wv-fs-md)', boxSizing: 'border-box' }}
        />
        <button type="submit" style={{ fontSize: 'var(--wv-fs-md)' }}>Subscribe</button>
      </form>
      {note && <div style={{ fontSize: 'var(--wv-fs-sm)', color: 'var(--wv-muted)', marginTop: 'var(--wv-sp-2)' }}>{note}</div>}
    </div>
  );
}
